import { useEffect, useRef } from "react";
import { AD_SLOTS } from "@/lib/adSlots";

// Same publisher ID as in public/index.html.
const ADSENSE_CLIENT = "ca-pub-6121091512281777";

/**
 * <AdSlot id="homeTop" /> — renders a single Google AdSense unit.
 *
 *  - `id` must match a key in /app/frontend/src/lib/adSlots.js.
 *  - Until the slot ID is filled in (post-approval), the component renders
 *    nothing so the layout is untouched.
 *  - Initialisation respects Google Consent Mode v2: defaults are set in
 *    public/index.html and updated by /components/CookieConsent.jsx, so we
 *    just push to `adsbygoogle` and the SDK does the right thing.
 *  - `className` lets callers control responsive visibility (e.g. hide the
 *    skyscraper rails on mobile).
 *  - `label` controls whether to show the small "Advertisement" badge — set
 *    to false for in-article placements that read more naturally without it.
 */
export default function AdSlot({ id, className = "", label = true }) {
  const cfg = AD_SLOTS[id];
  const insRef = useRef(null);
  const initialised = useRef(false);

  useEffect(() => {
    // Don't try to render if the slot has no ID yet (pre-approval state).
    if (!cfg || !cfg.slot) return;
    if (initialised.current) return;
    initialised.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      // Most common cause: adsbygoogle.js still loading. Retry once.
      setTimeout(() => {
        try { (window.adsbygoogle = window.adsbygoogle || []).push({}); }
        catch { /* give up silently — no point spamming the console */ }
      }, 1500);
    }
  }, [cfg]);

  if (!cfg || !cfg.slot) return null;

  return (
    <div className={`ad-slot ad-slot--${id} ${className}`} data-testid={`ad-slot-${id}`}>
      {label && (
        <div className="text-[10px] uppercase tracking-[0.22em] font-mono-ed text-neutral-400 text-center mb-2">
          Advertisement
        </div>
      )}
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={cfg.slot}
        data-ad-format={cfg.format || "auto"}
        {...(cfg.layout ? { "data-ad-layout": cfg.layout } : {})}
        data-full-width-responsive={cfg.responsive ? "true" : "false"}
      />
    </div>
  );
}
